/**
 * @fileoverview Presentation-only collapsible section panel for system-web.
 * @module system-web/components/CollapsibleSection
 *
 * Responsibilities:
 * - Render a titled, icon-led panel that can be expanded or collapsed.
 * - Display a summary badge in the header when collapsed.
 *
 * Dependencies:
 * - lucide-react (ChevronDown, ChevronRight).
 *
 * Architecture:
 * - Pure presentation component. It receives all state and callbacks as props.
 *
 * Notes:
 * - The toggle control is a button with aria-expanded and aria-controls for a11y.
 *
 * @reusable
 * Usage: Compose inside wizards, forms, or settings pages where grouped
 *        configuration needs to be shown/hidden.
 * Rules: presentation-only; no data fetching or persistence logic.
 */
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * @component CollapsibleSection
 *
 * Purpose:
 * - Show a collapsible content panel with a summary badge.
 *
 * Props:
 * - id:             Stable section identifier passed back to onToggle.
 * - isOpen:         Whether the section content is currently visible.
 * - onToggle:       Callback invoked when the header toggle is activated.
 * - icon:           Leading icon rendered in the section header.
 * - title:          Section title.
 * - badge:          Summary text shown in the header.
 * - badgeActive:    Whether the badge should be styled as active/selected.
 * - children:       Collapsible content.
 *
 * Behavior:
 * - Clicking the header toggles the content visibility.
 * - The badge is always visible in the header, even when collapsed.
 *
 * Data Source:
 * - local (props only).
 *
 * Constraints:
 * - No API calls; no hardcoded URLs; no mock data.
 *
 * Performance:
 * - Stateless; relies on parent re-render for open/close state.
 */
export interface CollapsibleSectionProps {
  id: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
  icon: React.ReactNode;
  title: string;
  badge: string;
  badgeActive?: boolean;
  children: React.ReactNode;
}

export default function CollapsibleSection({
  id,
  isOpen,
  onToggle,
  icon,
  title,
  badge,
  badgeActive,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={isOpen}
        aria-controls={`${id}-panel`}
        className="w-full px-4 py-3 flex items-center gap-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="text-gray-500">{icon}</div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900">{title}</span>
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            badgeActive
              ? 'bg-orange-100 text-orange-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {badge}
        </span>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
        )}
      </button>
      {isOpen && (
        <div id={`${id}-panel`} className="p-4 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
}
